import { useState, useRef } from "react";
import { X } from "lucide-react";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { LIBRARIES } from "../../components/googleMapsPLaces";
import { useUserStore } from "../../store/userStore";
import Select from "react-select";
import { useMetaDataStore } from "../../store/metadataStore";
import Loading from "../../components/loader";

interface AddressPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (addressData: {
    city: number | null;
    state: number | null;
    address: string;
    zipcode: string;
  }) => void;
  addressLoading:boolean
}

export default function AddressPopup({
  isOpen,
  onClose,
  onSubmit,
  addressLoading,
}: AddressPopupProps) {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLe_API as string,
    libraries: LIBRARIES,
  });
  const { user, updateUser } = useUserStore();
  const [formData, setFormData] = useState<{
    city: number | null;
    state: number | null;
    address: string;
    zipcode: string;
  }>({
    city: user?.cityId || null,
    state: user?.stateId || null,
    address: user?.address || "",
    zipcode: user?.zipcode || "",
  });
  const { states, getCitiesByState } = useMetaDataStore();

  const stateOptions = states.map((st) => ({
    value: st.zone_id,
    label: st.name,
    code: st.code,
  }));

  const cityOptions =
    formData?.state !== null
      ? getCitiesByState(formData.state).map((c) => ({
          value: c.city_id,
          label: c.city_name,
        }))
      : [];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const handlePlacesChanged = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place) return;
    const address = place.formatted_address || "";

    let google_city = "";
    let postal_code = "";
    console.log(place)
    place.address_components?.forEach((component) => {
      const types = component.types;

      if (types.includes("locality")) {
        google_city = component.long_name;
      }

      if (types.includes("postal_code")) {
        postal_code = component.long_name
      }
    });

    setFormData({ ...formData, address: address, zipcode: postal_code });

    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();

    // Start new session
    (autocompleteRef.current as any).sessionToken = sessionTokenRef.current;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-[95%] md:w-[550px] max-h-[90vh] overflow-y-auto relative animate-fadeIn">
        <button
          onClick={() => {
            setFormData({
              city: user?.cityId || null,
              state: user?.stateId || null,
              address: user?.address || "",
              zipcode: user?.zipcode || "",
            });
            onClose();
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-6 border-b border-gray-100 mt-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Add Address Details
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Please provide your complete address information below.
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <Select
                options={stateOptions}
                classNamePrefix="react-select"
                placeholder="select province"
                value={stateOptions.find((o) => o.value === formData.state)}
                onChange={(sel) => {
                  if (sel) {
                    setFormData({ ...formData, state: sel.value, city: null });
                    updateUser({ stateId: sel.value });
                  }
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <Select
                options={cityOptions}
                classNamePrefix="react-select"
                isDisabled={!formData.state}
                placeholder={
                  formData.state ? "Select city" : "select state first"
                }
                value={
                  cityOptions.find((o) => o.value === formData.city) || null
                }
                onChange={(sel) => {
                  if (sel) {
                    setFormData({ ...formData, city: sel.value });
                    updateUser({ cityId: sel.value });
                  }
                }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            {isLoaded && (
              <Autocomplete
                onLoad={(autocomplete) => {
                  autocompleteRef.current = autocomplete;

                  sessionTokenRef.current =
                    new google.maps.places.AutocompleteSessionToken();

                  // Set options WITHOUT sessionToken
                  autocomplete.setOptions({
                    componentRestrictions: { country: "ca" },
                    // types: ["address"],
                  });

                  // 👇 Set sessionToken separately
                  (autocomplete as any).sessionToken = sessionTokenRef.current;
                }}
                onPlaceChanged={handlePlacesChanged}
              >
                <input
                  type="text"
                  // placeholder="Enter street address"
                  disabled={!formData.state || !formData.city}
                  placeholder={
                    !formData.state
                      ? "Please select state first"
                      : !formData.city
                        ? "Please select city first"
                        : "Start typing your location"
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address:e.target.value})}
                />
              </Autocomplete>
            )}
          </div>
        </div>

        {addressLoading ? <Loading size={60} />  : <div className="p-6 flex justify-end gap-3 border-t border-gray-100">
          <button
            onClick={() => {
              setFormData({
                city: user?.cityId || null,
                state: user?.stateId || null,
                address: user?.address || "",
                zipcode: user?.zipcode || "",
              });
              onClose();
            }}
            className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          
          <button
            onClick={handleSubmit}
            disabled={addressLoading}
            className="bg-violet-600 hover:bg-violet-700 text-white font-semibold py-2.5 px-5 rounded-lg shadow-md transition"
          >
            Save Address
          </button>
        </div>}
      </div>
    </div>
  );
}
